import { stableRecordKey } from '../../lib/rowIdentity';
import React from 'react';
import { motion } from 'framer-motion';
import { Users, Heart, Calendar, Award } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { MandalaCorner } from '../../components/luxury/MandalaCorner';
import { PatolaBorder } from '../../components/luxury/PatolaBorder';
import { usePageContent, Paragraphs, PreviewBanner } from '../../lib/content';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const VALUES = [
  { icon: Users, title: 'Community', desc: 'Building strong connections within the Gujarati community' },
  { icon: Heart, title: 'Culture', desc: 'Preserving and celebrating our rich cultural heritage' },
  { icon: Calendar, title: 'Events', desc: 'Organizing festivals and celebrations throughout the year' },
  { icon: Award, title: 'Excellence', desc: 'Delivering high-quality experiences for all members' },
];

export default function LuxuryAbout() {
  const { t, c, isPreview } = usePageContent('about');
  const values = Array.isArray(c.values) && c.values.length ? c.values.map((v, i) => ({ ...v, icon: VALUES[i % VALUES.length].icon })) : VALUES;
  return (
    <div className="overflow-hidden">
      {isPreview && <PreviewBanner page="About" />}
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--lux-royal-soft))] to-[hsl(var(--lux-ivory))] overflow-hidden">
        <MandalaCorner position="top-left" className="opacity-20" />
        <MandalaCorner position="bottom-right" className="opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-5xl sm:text-6xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="luxury-about-title">
              {t('hero_title', 'About')} <span className="text-[hsl(var(--lux-gold))]">{t('hero_title_accent', 'Gujarat Samaj')}</span>
            </h1>
            <p className="text-lg text-[hsl(var(--lux-ink)/0.8)] max-w-3xl mx-auto mb-8">
              {t('hero_subtitle', 'A vibrant community organisation dedicated to preserving and promoting Gujarati culture in Nottingham.')}
            </p>
            <div className="lux-gold-divider mx-auto max-w-md" />
          </motion.div>
        </div>
      </section>

      {/* What We Are About */}
      <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-8" style={{ fontFamily: 'Cormorant Garamond' }}>
              {t('story_heading', 'What we are about')}
            </h2>
            <div className="prose prose-lg max-w-none text-[hsl(var(--lux-ink)/0.8)] space-y-4">
              {c.story_text ? <Paragraphs text={c.story_text} /> : (<>
              <p>Welcome to Gujarat Samaj Nottingham and thank you for visiting our website.</p>
              <p>At the heart of any community organisation are its people. The GS Executive Committee are made up of dedicated volunteers who engage with members to embrace and deliver a variety of different cultural experiences. You can find names and contact details of the committee members on the contact us page.</p>
              <p>Alongside our annual events, <strong>Holi</strong> (festival of colours), <strong>Navratri</strong> (festival of dance) and <strong>Diwali</strong> (festival of light) the committee has introduced a variety of additional activities such as a community BBQ, discounted comedy nights and theatre experiences.</p>
              <p>Our work with the Hindu Temple Nottingham to deliver our Holi and taking part in the South Asian Heritage Festival which began 4 years ago, allows us, along with other South Asian organisations to come together in celebration of the South Asian experience, its history and its people in the UK.</p>
              </>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Values */}
      {t('show_values', true) && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fadeInUp} className="text-3xl font-bold text-center text-[hsl(var(--lux-royal))] mb-12" style={{ fontFamily: 'Cormorant Garamond' }}>
            {t('values_heading', 'Our Values')}
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <motion.div key={stableRecordKey(value)} {...fadeInUp} transition={{ delay: idx * 0.1 }}>
                <Card className="lux-ornate-card h-full hover:shadow-[var(--lux-shadow-2)] transition-all">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center mx-auto mb-4">
                      <value.icon className="w-8 h-8 text-[hsl(var(--lux-royal))]" />
                    </div>
                    <h3 className="text-lg font-bold text-[hsl(var(--lux-royal))] mb-2 text-center" style={{ fontFamily: 'Cormorant Garamond' }}>
                      {value.title}
                    </h3>
                    <p className="text-sm text-[hsl(var(--lux-ink)/0.7)] text-center">{value.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Charity Info */}
      <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="lux-ornate-card border-2 border-[hsl(var(--lux-gold))] bg-gradient-to-br from-[hsl(var(--lux-royal))] to-[hsl(var(--lux-midnight))] text-white">
              <PatolaBorder />
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>
                  {t('charity_heading', 'Registered Charity')}
                </h2>
                <p className="text-white/80 mb-4 leading-relaxed">
                  {t('charity_text', 'Gujarat Samaj Nottingham is a registered charity committed to serving the Gujarati community in Nottingham and surrounding areas.')}
                </p>
                <Badge className="bg-[hsl(var(--lux-gold))] text-white border-[hsl(var(--lux-gold))] text-base px-4 py-2">
                  Charity Registration Number: {t('charity_number', '513936')}
                </Badge>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
